/**
 * 1.8.2 (D-1.8.2-3) — one calendar for the house: the shape of `GET /api/calendar?month=yyyy-MM` and the pure rules
 * the API and the page share (who sees which names, where each line points, the month's grid, the day's overview in
 * time order, the filters kept on the device).
 *
 * Seven categories, each a word on the screen (never a colour alone): Housekeeping (the day's count from the Day
 * plan), Companion (each walk or wellness visit), Reading & Services (each booking), Tech Support (each ticket),
 * Events (each event), Closed (closed days) and Holidays (the federal holidays the app already proposes, 1.5.0).
 *
 * Read-only: nothing here books, moves or sends — a line only points at the page that does.
 *
 * No dependencies beyond the shared rules, so the app and the API share it.
 */
import { addDaysToKey, dayOfWeek, isDateKey } from './dates';
import { ROLE_CAN, type Role } from './roles';
import type { CategoryKey, TripKind } from './volunteers';
import type { HelpKey } from './tech';
import type { ClosedScope } from './closed';

export const CAL_CATEGORIES = ['housekeeping', 'companion', 'reading', 'tech', 'events', 'closed', 'holidays'] as const;

/** 26 Oct 2026 (D-1025-WIRING): the scheduler and the viewer see Housekeeping only — no Companion, Reading or Tech Support line on their calendar, and no tick for them. */
export const HOUSEKEEPING_ONLY_CATEGORIES = ['companion', 'reading', 'tech'] as const;
export const seesCategory = (role: string | null | undefined, c: (typeof CAL_CATEGORIES)[number]): boolean => !((role === 'scheduler' || role === 'viewer') && (HOUSEKEEPING_ONLY_CATEGORIES as readonly string[]).includes(c));
export type CalCategory = (typeof CAL_CATEGORIES)[number];
export const isCalCategory = (s: unknown): s is CalCategory => typeof s === 'string' && (CAL_CATEGORIES as readonly string[]).includes(s);

/**
 * Whose names a role sees — the names it already sees on the source page, never more:
 *   full    the administrator and a co-administrator (People, Volunteers, the tickets): the tenant's name on every
 *           line, the volunteer's first name;
 *   office  the scheduler: the tenant's name on a Tech Support ticket (the ticket page shows it to the office) and the
 *           volunteer's first name; no tenant name on a Companion or Reading line (the scheduler has no Volunteers);
 *   titles  the viewer and the crew (and anyone else): counts for Housekeeping and titles for the rest — no names.
 */
export type NameLevel = 'full' | 'office' | 'titles';
export function nameLevelOf(role: Role | null | undefined): NameLevel {
  if (role === 'admin' || role === 'coadmin') return 'full';
  if (role === 'scheduler') return 'office';
  return 'titles';
}

/** One line of a day, as the page draws it. The fields beyond the first five belong to one category or another. */
export interface CalLine {
  /** Unique in the month (`hk-20261112`, `tech-<AppointmentID>`, `ev-<EventID>` …). */
  id: string;
  category: CalCategory;
  dayKey: string;
  /** Minutes after midnight, New York; null = the whole day (a closed day, a holiday). */
  minutes: number | null;
  endMinutes: number | null;
  /** Housekeeping: the day's booked visits, the ones still in pencil, and how many crews. */
  visits?: number;
  proposed?: number;
  crews?: 1 | 2;
  /** Companion / Reading & Services: which kind of booking (shared/volunteers.ts CATEGORIES). */
  activity?: CategoryKey;
  trip?: TripKind | null;
  /** No volunteer confirmed yet (a request still out). */
  waiting?: boolean;
  /** The confirmed volunteer's first name — not for the `titles` level. */
  volunteerFirst?: string | null;
  /** The tenant's name — only where the role sees it on the source page. */
  tenantName?: string | null;
  /** Tech Support: what the help is with. */
  help?: HelpKey[];
  /** Events: the title; Closed: the reason; Holidays: the holiday's name. */
  name?: string;
  place?: string;
  /** Closed: Housekeeping only, or everything. */
  scope?: ClosedScope;
  /** Where the line points (an address inside the app), or null when the role cannot open that page. */
  href: string | null;
}

/** What the API composes before it knows who asks: the line plus what its address is made from. */
export interface RawCalLine extends Omit<CalLine, 'href'> {
  ref?: { volunteerId?: string | null; appointmentId?: string | null; eventId?: string | null };
}

export interface HouseCalendarResponse {
  /** yyyy-MM */
  month: string;
  todayKey: string;
  names: NameLevel;
  lines: CalLine[];
  /** The categories that could not be read this time (a list not on the site yet) — the page says so. */
  unavailable?: CalCategory[];
}

/* ------------------------------------------------------------------------------------------------
 * Months
 * ---------------------------------------------------------------------------------------------- */

/** `2026-11` → true. */
export const isCalMonth = (s: unknown): s is string => typeof s === 'string' && /^\d{4}-\d{2}$/.test(s) && isDateKey(`${s.slice(0, 4)}${s.slice(5, 7)}01`);
export const calMonthOf = (dayKey: string) => `${dayKey.slice(0, 4)}-${dayKey.slice(4, 6)}`;
export const firstKeyOf = (month: string) => `${month.slice(0, 4)}${month.slice(5, 7)}01`;
export function lastKeyOf(month: string): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${month.slice(0, 4)}${month.slice(5, 7)}${String(days).padStart(2, '0')}`;
}
export function shiftCalMonth(month: string, delta: number): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7)) - 1 + delta;
  const d = new Date(Date.UTC(y, m, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
/** Every day of the month, in order. */
export function daysOfMonth(month: string): string[] {
  const out: string[] = [];
  for (let k = firstKeyOf(month), last = lastKeyOf(month); k <= last; k = addDaysToKey(k, 1)) out.push(k);
  return out;
}
/** The column a day stands in on a Sunday-first week (0 = Sunday). */
export const weekColumn = (dayKey: string) => dayOfWeek(dayKey);

/* ------------------------------------------------------------------------------------------------
 * Where a line points, and the names on it
 * ---------------------------------------------------------------------------------------------- */

const canOpen = (role: Role | null | undefined, route: string): boolean => !!role && role !== 'tech' && (ROLE_CAN[role]?.routes as readonly string[] | undefined)?.includes(route) === true;

/**
 * The address a line points at for this role (null: the role cannot open that page — the line is still shown).
 *   Housekeeping  Housekeeping's Day for today (`/housekeeping`); a day before today is the Day's record of it
 *                 (`/housekeeping?day=`); a day after today is the Schedule's week of that day (`/schedule?day=`).
 *                 (26 Oct 2026, D-1025-WIRING: the program's own address, so a plain link never lands on another
 *                 program's page when the person last chose Companion.)
 *   Companion / Reading  the volunteer's page (`/volunteers/<id>`), or the program's own page (`/companion`,
 *                 `/reading`) when nobody is confirmed yet; a role without that page gets no link.
 *   Tech Support  the ticket (`/tech/<AppointmentID>`).
 *   Events        the event's page (`/events/<EventID>`).
 *   Closed / Holidays  Admin → Settings (Housekeeping day → Closed days), for the administrator.
 */
export function hrefOf(raw: RawCalLine, role: Role | null | undefined, todayKey: string): string | null {
  switch (raw.category) {
    case 'housekeeping':
      if (raw.dayKey === todayKey) return canOpen(role, '/housekeeping') ? '/housekeeping' : null;
      if (raw.dayKey < todayKey) return canOpen(role, '/housekeeping') ? `/housekeeping?day=${raw.dayKey}` : null;
      return canOpen(role, '/schedule') ? `/schedule?day=${raw.dayKey}` : null;
    case 'companion':
    case 'reading':
      if (raw.ref?.volunteerId && canOpen(role, '/volunteers/:volunteerId')) return `/volunteers/${encodeURIComponent(raw.ref.volunteerId)}`;
      return canOpen(role, raw.category === 'reading' ? '/reading' : '/companion') ? (raw.category === 'reading' ? '/reading' : '/companion') : null;
    case 'tech':
      return raw.ref?.appointmentId && canOpen(role, '/tech/:appointmentId') ? `/tech/${encodeURIComponent(raw.ref.appointmentId)}` : null;
    case 'events':
      return raw.ref?.eventId && canOpen(role, '/events/:eventId') ? `/events/${encodeURIComponent(raw.ref.eventId)}` : null;
    case 'closed':
    case 'holidays':
      return canOpen(role, '/admin') ? '/admin/settings' : null;
  }
}

/** The line as this role may see it: the names it already sees on the source page, and the address it can open. */
export function lineFor(raw: RawCalLine, role: Role | null | undefined, todayKey: string): CalLine {
  const level = nameLevelOf(role);
  const { ref: _ref, tenantName, volunteerFirst, ...rest } = raw;
  void _ref;
  const out: CalLine = { ...rest, href: hrefOf(raw, role, todayKey) };
  const tenantOk = level === 'full' || (level === 'office' && raw.category === 'tech');
  if (tenantName !== undefined) out.tenantName = tenantOk ? tenantName : null;
  if (volunteerFirst !== undefined) out.volunteerFirst = level === 'titles' ? null : volunteerFirst;
  return out;
}

/* ------------------------------------------------------------------------------------------------
 * The day's overview and the filters
 * ---------------------------------------------------------------------------------------------- */

const order = (c: CalCategory) => CAL_CATEGORIES.indexOf(c);
const nameOf = (l: CalLine) => l.name ?? l.tenantName ?? l.id;

/** Time order: the whole-day lines first (holidays, closed — by category), then by start time, then by category. */
export function compareLines(a: CalLine, b: CalLine): number {
  if (a.minutes === null && b.minutes !== null) return -1;
  if (b.minutes === null && a.minutes !== null) return 1;
  if (a.minutes === null && b.minutes === null) return (a.category === 'holidays' ? -1 : 0) - (b.category === 'holidays' ? -1 : 0) || order(a.category) - order(b.category) || nameOf(a).localeCompare(nameOf(b));
  return (a.minutes ?? 0) - (b.minutes ?? 0) || order(a.category) - order(b.category) || nameOf(a).localeCompare(nameOf(b)) || a.id.localeCompare(b.id);
}

/** The lines of one day, in time order. */
export function dayOverview(lines: readonly CalLine[], dayKey: string): CalLine[] {
  return lines.filter((l) => l.dayKey === dayKey).sort(compareLines);
}

/** Only the ticked categories. */
/** One count of a small day cell (1.8.7): a program's number, or a closed day / a holiday (shown as a word). */
export interface DayCount {
  category: CalCategory;
  count: number;
  /** Housekeeping with nothing booked yet: the count is the visits still in pencil. */
  pencil?: boolean;
}

/**
 * 1.8.7 (D-1.8.7-1): what a small day cell says - one count a category, in the categories' order. Housekeeping's is
 * the day's booked visits (the ones in pencil when nothing is booked yet); every other program's is its lines.
 */
export function dayCounts(lines: readonly CalLine[]): DayCount[] {
  const out: DayCount[] = [];
  for (const category of CAL_CATEGORIES) {
    const mine = lines.filter((l) => l.category === category);
    if (!mine.length) continue;
    if (category !== 'housekeeping') {
      out.push({ category, count: mine.length });
      continue;
    }
    const visits = mine.reduce((n, l) => n + (l.visits ?? 0), 0);
    const proposed = mine.reduce((n, l) => n + (l.proposed ?? 0), 0);
    if (visits) out.push({ category, count: visits });
    else if (proposed) out.push({ category, count: proposed, pencil: true });
  }
  return out;
}

export function filterLines<T extends { category: CalCategory }>(lines: readonly T[], shown: ReadonlySet<CalCategory>): T[] {
  return lines.filter((l) => shown.has(l.category));
}

/** The device's choice, read back: an unreadable or empty value is every category. */
export function parseShown(stored: string | null | undefined): Set<CalCategory> {
  if (!stored) return new Set(CAL_CATEGORIES);
  try {
    const v = JSON.parse(stored) as unknown;
    if (!Array.isArray(v)) return new Set(CAL_CATEGORIES);
    return new Set(v.filter(isCalCategory));
  } catch {
    return new Set(CAL_CATEGORIES);
  }
}
export const serializeShown = (shown: ReadonlySet<CalCategory>) => JSON.stringify(CAL_CATEGORIES.filter((c) => shown.has(c)));
/** The device key the choice is kept under. */
export const SHOWN_KEY = 'abf.cal182.shown';
